<template>
    <div>
        <SellersHeaderV2
            title="Fornecedores"
            subtitle="Fornecedores da sua rede e os vendedores de cada um"
            :filters="filters"
            :appliedFilters="appliedFilters"
            :states="states"
            :isDesktop="isDesktop"
            :showTotalizadores="showTotalizadores"
            showProviderFilters
            @filtrar="filtrar"
            @limpar="handleLimparFiltros"
            @reload="loadData()"
            @toggle-totalizadores="showTotalizadores = !showTotalizadores"
        />

        <div v-if="isDesktop" class="page-section">
            <dashboard-stats
                :items="dashboardCards"
                :showTotalizadores="showTotalizadores"
                :selectedKeys="selectedCardKeys"
                dynamic-width
                @click="aplicarFiltroRapido"
            />
        </div>

        <div class="page-section">
            <a-card :bordered="false" style="width: 100%" class="table-card">
                <div class="table-container">
                    <a-table
                        size="small"
                        :columns="columns"
                        :data-source="providers"
                        row-key="id"
                        :loading="loading"
                        :pagination="pagination"
                        :scroll="{ x: 'max-content' }"
                        :expandIcon="renderExpandIcon"
                        :expandedRowKeys="expandedRowKeys"
                        :locale="{ emptyText: 'Nenhum fornecedor encontrado' }"
                        @expand="onExpand"
                        @change="handleTableChange"
                    >
                        <template slot="cover" slot-scope="text, record">
                            <img v-if="hasCover(record)" class="provider-cover" :src="record.cover_url" />
                            <span v-else class="material-icons provider-cover-icon">local_shipping</span>
                        </template>
                        <template slot="cnpj" slot-scope="text">
                            {{ text | VMask("##.###.###/####-##") }}
                        </template>
                        <template slot="state" slot-scope="text, record">
                            {{ record.head_office && record.head_office.state ? record.head_office.state.name : "--" }}
                        </template>
                        <template slot="city" slot-scope="text, record">
                            {{ record.head_office && record.head_office.city ? record.head_office.city.name : "--" }}
                        </template>
                        <template slot="phone" slot-scope="text">
                            <a v-if="text" :href="'https://api.whatsapp.com/send?phone=' + formatNumber(text)" target="_blank" class="phone-link">
                                <i class="fab fa-whatsapp"></i>{{ text | VMask("(##) ####-#####") }}
                            </a>
                            <span v-else>--</span>
                        </template>
                        <template slot="status" slot-scope="text, record">
                            <a-tag v-if="record.blocked !== undefined" :color="isBlocked(record) ? 'red' : 'green'">
                                {{ isBlocked(record) ? "Bloqueado" : "Ativo" }}
                            </a-tag>
                        </template>
                        <template slot="homologation" slot-scope="text, record">
                            <a-tooltip v-if="homologationStatus(record)" placement="top">
                                <template slot="title">
                                    <div>Cotações: {{ linkStatusLabel(record.purchase_link.quotation_status) }}</div>
                                    <div>Pedidos: {{ linkStatusLabel(record.purchase_link.order_status) }}</div>
                                    <div v-if="record.purchase_link.homologation_updated_at">
                                        Atualizado em {{ record.purchase_link.homologation_updated_at | formatDate }}
                                    </div>
                                </template>
                                <a-tag :color="homologationTag(record).color">{{ homologationTag(record).label }}</a-tag>
                            </a-tooltip>
                            <span v-else>--</span>
                        </template>
                        <template slot="acoes" slot-scope="text, record">
                            <ActionMenu :actions="getActions(record)" />
                        </template>

                        <div slot="expandedRowRender" slot-scope="record">
                            <a-table
                                size="small"
                                :columns="sellerColumns"
                                :data-source="record.sellers"
                                row-key="id"
                                :pagination="false"
                            >
                                <template slot="id" slot-scope="text">#{{ text }}</template>
                                <template slot="phone" slot-scope="text">
                                    <a v-if="text" :href="'https://api.whatsapp.com/send?phone=' + formatNumber(text)" target="_blank" class="phone-link">
                                        <i class="fab fa-whatsapp"></i>{{ text | VMask("(##) ####-#####") }}
                                    </a>
                                    <span v-else>--</span>
                                </template>
                                <template slot="type" slot-scope="text, seller">
                                    {{ sellerType(seller) == "REQUEST" ? "Cotação" : "Vendedor exclusivo" }}
                                </template>
                                <template slot="participation" slot-scope="text, seller">
                                    <a-tag v-if="getParticipation(seller) > 0" :color="participationColor(getParticipation(seller))">
                                        {{ (getParticipation(seller) * 100).toFixed(2) }} %
                                    </a-tag>
                                    <span v-else>--</span>
                                </template>
                                <template slot="rating" slot-scope="text, seller">
                                    {{ rating(seller) }}
                                </template>
                                <template slot="acoes" slot-scope="text, seller">
                                    <ActionMenu :actions="getSellerActions(seller, record)" />
                                </template>
                            </a-table>
                        </div>
                    </a-table>
                </div>
            </a-card>
        </div>

        <cc-loader-full v-if="loadingFull" />
        <ProviderOrdersV2Modal v-if="showOrderModal" :provider="showOrderModal" @close="showOrderModal = false" />
        <ProviderProductsV2Modal v-if="showProductsModal" :provider="showProductsModal" @close="showProductsModal = false" />
    </div>
</template>

<script>
import { perm_mixin } from "@/mixins/permission.mixin";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import StatsCards from "@/components/cliente/monitors/StatsCards.vue";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import ProviderOrdersV2Modal from "../modals/provider-orders-v2.modal.vue";
import ProviderProductsV2Modal from "../modals/provider-products-v2.modal.vue";
import SellersHeaderV2 from "../SellersHeaderV2.vue";
import { sellersListMixin } from "../sellers-list.mixin";

const PLACEHOLDER_COVER = "https://via.placeholder.com/100x50/FFFFFFFF/ff7110/?text=CLUB";

// Status de cotação/pedido do vínculo cliente x fornecedor (client_purchase_providers).
const LINK_STATUS = { PENDING: "PENDING", HOMOLOGATED: "HOMOLOGATED", BLOCKED: "BLOCKED" };
const LINK_STATUS_LABELS = { PENDING: "Pendente", HOMOLOGATED: "Liberado", BLOCKED: "Bloqueado" };
// Status geral derivado pelo kyojin a partir dos dois status acima.
const HOMOLOGATION_TAGS = {
    HOMOLOGATED: { label: "Homologado", color: "green" },
    PENDING: { label: "Pendente", color: "orange" },
    REFUSED: { label: "Recusado", color: "red" },
    PARTIAL: { label: "Parcial", color: "blue" },
};

export default {
    name: "ProvidersV2Tab",
    mixins: [perm_mixin, loaderMixin, sellersListMixin],
    components: {
        "dashboard-stats": StatsCards,
        ActionMenu,
        SellersHeaderV2,
        ProviderOrdersV2Modal,
        ProviderProductsV2Modal,
    },
    data() {
        return {
            loadingFull: false,
            providers: [],
            expandedRowKeys: [],
            showOrderModal: false,
            showProductsModal: false,
            showHomologationCard: true,
            sort: undefined,
            columns: [
                { title: "", key: "cover", align: "center", scopedSlots: { customRender: "cover" } },
                { title: "Fornecedor", dataIndex: "name", key: "name", sorter: true },
                { title: "CNPJ", dataIndex: "cnpj", key: "cnpj", sorter: true, scopedSlots: { customRender: "cnpj" } },
                { title: "Estado", key: "state", sorter: true, scopedSlots: { customRender: "state" } },
                { title: "Cidade", key: "city", sorter: true, scopedSlots: { customRender: "city" } },
                { title: "Telefone", dataIndex: "phone", key: "phone", sorter: true, scopedSlots: { customRender: "phone" } },
                { title: "Status", key: "status", align: "center", scopedSlots: { customRender: "status" } },
                { title: "Homologação", key: "homologation", align: "center", scopedSlots: { customRender: "homologation" } },
                { title: "Ações", key: "acoes", align: "center", width: 70, scopedSlots: { customRender: "acoes" } },
            ],
            sellerColumns: [
                { title: "Id", dataIndex: "id", key: "id", scopedSlots: { customRender: "id" } },
                { title: "Nome do vendedor", dataIndex: "name", key: "name" },
                { title: "Telefone", dataIndex: "phone", key: "phone", scopedSlots: { customRender: "phone" } },
                { title: "Email", dataIndex: "email", key: "email" },
                { title: "Tipo", dataIndex: "type", key: "type", scopedSlots: { customRender: "type" } },
                { title: "Participação", key: "participation", align: "center", scopedSlots: { customRender: "participation" } },
                { title: "Nota", key: "rating", align: "center", scopedSlots: { customRender: "rating" } },
                { title: "Ação", key: "acoes", align: "center", width: 70, scopedSlots: { customRender: "acoes" } },
            ],
        };
    },
    methods: {
        hasCover(provider) {
            return provider.cover_url && provider.cover_url !== PLACEHOLDER_COVER;
        },
        isBlocked(provider) {
            return !!provider.blocked;
        },
        hasSellers(provider) {
            return !!(provider.sellers && provider.sellers.length);
        },
        buildParams(page) {
            const { name, status, state, city, phone } = this.appliedFilters;
            return {
                page,
                per_page: this.pagination.pageSize,
                sort: this.sort,
                "filter[search]": name || undefined,
                "filter[blocked]": status ? status === "BLOCKED" : undefined,
                "filter[state]": state || undefined,
                "filter[city]": city || undefined,
                "filter[phone]": phone || undefined,
                "filter[with_order]": this.withOrders ? 1 : undefined,
                "filter[with_offer]": this.withOffer ? 1 : undefined,
                "filter[homologation_status]": this.appliedFilters.homologation_status || undefined,
            };
        },
        readMeta(meta = {}) {
            this.pagination.total = meta.total || 0;
            this.totalSellers = meta.total_providers || 0;
            this.totalOrders = meta.with_orders || 0;
            this.totalOffers = meta.with_offers || 0;
            this.totalPendingHomologation = meta.pending_homologation || 0;
        },
        resetSort() {
            this.sort = undefined;
            this.columns = this.columns.map((column) => ({ ...column, sortOrder: column.sorter ? false : undefined }));
        },
        loadData(page = this.pagination.current) {
            this.loading = true;
            this.svc
                .loadPurchaseProviders(this.buildParams(page))
                .then(({ data }) => {
                    this.providers = data.data || [];
                    this.expandedRowKeys = [];
                    this.pagination.current = page;
                    this.readMeta(data.meta);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleTableChange(pagination, filters, sorter) {
            this.pagination.pageSize = pagination.pageSize;
            this.sort = sorter && sorter.order ? `${sorter.order === "descend" ? "-" : ""}${sorter.columnKey}` : undefined;
            this.columns = this.columns.map((column) =>
                column.sorter ? { ...column, sortOrder: column.key === sorter.columnKey ? sorter.order : false } : column
            );
            this.loadData(pagination.current);
        },
        renderExpandIcon({ record, expanded, onExpand }) {
            const h = this.$createElement;
            if (this.isBlocked(record) || !this.hasSellers(record)) {
                return h("span", { class: "ant-table-row-expand-icon ant-table-row-spaced" });
            }
            return h("span", {
                class: ["ant-table-row-expand-icon", expanded ? "ant-table-row-expanded" : "ant-table-row-collapsed"],
                attrs: { title: expanded ? "Esconder vendedores" : "Ver vendedores" },
                on: { click: (e) => onExpand(record, e) },
            });
        },
        onExpand(expanded, record) {
            if (!expanded) {
                this.expandedRowKeys = this.expandedRowKeys.filter((key) => key !== record.id);
                return;
            }
            if (this.isBlocked(record) || !this.hasSellers(record)) return;
            this.expandedRowKeys = [...this.expandedRowKeys, record.id];
        },
        sellerType(seller) {
            return seller.client_pivot ? seller.client_pivot.STYPE : null;
        },
        rating(seller) {
            return seller.average_rating ? seller.average_rating + ".0" : "--";
        },
        homologationStatus(provider) {
            return provider.purchase_link ? provider.purchase_link.homologation_status : null;
        },
        homologationTag(provider) {
            return HOMOLOGATION_TAGS[this.homologationStatus(provider)] || { label: "--", color: "" };
        },
        linkStatusLabel(status) {
            return LINK_STATUS_LABELS[status] || "--";
        },
        getActions(provider) {
            const blocked = this.isBlocked(provider);
            return [
                {
                    label: blocked ? "Desbloquear fornecedor" : "Bloquear fornecedor",
                    icon: blocked ? "verified" : "block",
                    iconClass: blocked ? "action-success" : "action-danger",
                    visible: !!provider.blacklists && this.can("CLIENT_SELLER", "UPDATE"),
                    onClick: () => (blocked ? this.removeFromBlacklist(provider) : this.blacklist(provider)),
                },
                { label: "Lista de pedidos", icon: "shopping_cart", iconClass: "action-success", onClick: () => (this.showOrderModal = provider) },
                { label: "Lista de produtos", icon: "list_alt", iconClass: "action-info", onClick: () => (this.showProductsModal = provider) },
                ...this.getHomologationActions(provider),
            ];
        },
        getHomologationActions(provider) {
            const link = provider.purchase_link;
            if (!link || !link.homologation_status || !this.can("CLIENT_SELLER", "UPDATE")) return [];
            const { HOMOLOGATED, BLOCKED } = LINK_STATUS;
            const quotation = link.quotation_status;
            const order = link.order_status;
            return [
                { divider: true },
                {
                    label: "Homologar",
                    icon: "verified_user",
                    iconClass: "action-success",
                    visible: link.homologation_status !== "HOMOLOGATED",
                    onClick: () =>
                        this.updateHomologation(provider, { quotation_status: HOMOLOGATED, order_status: HOMOLOGATED }, "Fornecedor homologado."),
                },
                {
                    label: "Recusar",
                    icon: "gpp_bad",
                    iconClass: "action-danger",
                    visible: link.homologation_status !== "REFUSED",
                    onClick: () => this.refuse(provider),
                },
                {
                    label: "Liberar cotações",
                    icon: "request_quote",
                    iconClass: "action-success",
                    visible: quotation !== HOMOLOGATED,
                    onClick: () => this.updateHomologation(provider, { quotation_status: HOMOLOGATED }, "Cotações liberadas."),
                },
                {
                    label: "Bloquear cotações",
                    icon: "request_quote",
                    iconClass: "action-danger",
                    visible: quotation !== BLOCKED,
                    onClick: () => this.updateHomologation(provider, { quotation_status: BLOCKED }, "Cotações bloqueadas."),
                },
                {
                    label: "Liberar pedidos",
                    icon: "local_shipping",
                    iconClass: "action-success",
                    visible: order !== HOMOLOGATED,
                    onClick: () => this.updateHomologation(provider, { order_status: HOMOLOGATED }, "Pedidos liberados."),
                },
                {
                    label: "Bloquear pedidos",
                    icon: "local_shipping",
                    iconClass: "action-danger",
                    visible: order !== BLOCKED,
                    onClick: () => this.updateHomologation(provider, { order_status: BLOCKED }, "Pedidos bloqueados."),
                },
            ];
        },
        refuse(provider) {
            this.$swal
                .fire({
                    title: "Recusar fornecedor?",
                    text: `O fornecedor ${provider.name} terá cotações e pedidos bloqueados.`,
                    type: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Sim, confirmar!",
                    cancelButtonText: "Cancelar",
                })
                .then((result) => {
                    if (!result.isConfirmed) return;
                    this.updateHomologation(
                        provider,
                        { quotation_status: LINK_STATUS.BLOCKED, order_status: LINK_STATUS.BLOCKED },
                        "Fornecedor recusado."
                    );
                });
        },
        updateHomologation(provider, statuses, successMessage) {
            this.loadingFull = true;
            this.svc
                .homologate([provider.id], statuses)
                .then(() => {
                    this.snack(successMessage, "success");
                    this.loadData();
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                    this.snack(this.homologationErrorMessage(error), "error");
                })
                .finally(() => {
                    this.loadingFull = false;
                });
        },
        homologationErrorMessage(error) {
            const response = error && error.response;
            if (response && response.status === 403) return "Você não tem permissão para alterar a homologação de fornecedores.";
            if (response && response.data && response.data.message) return response.data.message;
            return "Ocorreu um erro ao atualizar a homologação do fornecedor.";
        },
        getSellerActions(seller, provider) {
            return [
                { label: "Atualizar tipo do vendedor", icon: "swap_horiz", iconClass: "action-info", onClick: () => this.syncSuplier(seller) },
                { divider: true },
                { label: "Excluir vendedor", icon: "delete", iconClass: "action-danger", onClick: () => this.removeSeller(seller, provider) },
            ];
        },
        syncSuplier(seller) {
            const type = this.sellerType(seller) == "REQUEST" ? "DIRECT_REQUEST" : "REQUEST";
            this.prov_svc
                .syncProvider(seller.id, type)
                .then(() => {
                    this.$set(seller, "client_pivot", { ...seller.client_pivot, STYPE: type });
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        removeSeller(seller, provider) {
            this.confirm_action({
                message: `Confirma remoção do vendedor ${seller.name}?`,
                callback: () =>
                    this.prov_svc.remove_seller(seller.id).then(() => {
                        provider.sellers = provider.sellers.filter((s) => s.id != seller.id);
                    }),
            });
        },
        blacklist(provider) {
            this.$swal
                .fire({
                    title: "Bloquear fornecedor?",
                    text: `O fornecedor ${provider.name} será bloqueado!`,
                    type: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Sim, confirmar!",
                    input: "text",
                    inputPlaceholder: "Justificativa",
                })
                .then((result) => {
                    if (!result.isConfirmed) return;
                    this.loadingFull = true;
                    this.svc
                        .blacklist(provider.id, result.value)
                        .then(() => {
                            this.$swal.fire("Bloqueado!", "Fornecedor bloqueado com sucesso.", "success");
                            this.loadData();
                        })
                        .catch(() => {
                            this.$swal.fire("Erro!", "Ocorreu um erro ao bloquear o fornecedor.", "error");
                        })
                        .finally(() => {
                            this.loadingFull = false;
                        });
                });
        },
        removeFromBlacklist(provider) {
            this.$swal
                .fire({
                    title: "Desbloquear fornecedor?",
                    text: `O fornecedor ${provider.name} será desbloqueado!`,
                    type: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Sim, confirmar!",
                })
                .then((result) => {
                    if (!result.isConfirmed) return;
                    this.loadingFull = true;
                    this.svc
                        .remove_from_blacklist(provider.id)
                        .then(() => {
                            this.$swal.fire("Desbloqueado!", "Fornecedor desbloqueado com sucesso.", "success");
                            this.loadData();
                        })
                        .catch(() => {
                            this.$swal.fire("Erro!", "Ocorreu um erro ao desbloquear o fornecedor.", "error");
                        })
                        .finally(() => {
                            this.loadingFull = false;
                        });
                });
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/sass/commons/_list-page-v2";

.provider-cover {
    width: 60px;
    height: 40px;
    object-fit: contain;
    border-radius: 6px;
}
.provider-cover-icon {
    color: #bfbfbf;
    font-size: 1.8rem;
}
.phone-link {
    white-space: nowrap;
    color: #30aa4c;
}
.phone-link .fab {
    margin-right: 6px;
}
.table-card ::v-deep(.ant-table-expanded-row > td) {
    padding: 0 !important;
    background-color: transparent !important;
}.ant-table-row-expand-icon.ant-table-row-spaced {
    cursor: default;
}
</style>
